/**
 * useAttackEvents — detects newly-arrived `attack` events in GameState
 * and returns animation entries for the attack badge layer.
 *
 * Contract:
 *   - The FIRST observed `events` array is treated as the "already seen"
 *     baseline so historical attacks don't replay on mount.
 *   - Each `attack` event produces ONE entry with both attacker and
 *     defender fields so both badges can animate simultaneously.
 *   - `conquered` is forwarded so the layer can suppress any conquest
 *     visual on the defender side — ConquestFlashLayer handles that.
 *   - A Set of already-animated ts values prevents re-fires on StrictMode
 *     double-invoke, reconnects, or cache replays.
 *
 * Per-roll vs cumulative note:
 *   The wire shape `AttackResponse.attackerLost` / `defenderLost` are
 *   CUMULATIVE across all blitz iterations in a single HTTP request.
 *   However, the engine appends ONE `attack` GameEvent PER ROLL into
 *   GameState.events (see apps/api/src/routes/games.ts blitz loop, which
 *   calls applyAttack N times). Each event carries only that single roll's
 *   losses, not the request total. A blitz attack with 5 rolls produces 5
 *   separate events, all with very close `ts` values (same wall-clock ms in
 *   practice, since the engine is synchronous).
 *
 *   To show a badge with the cumulative total (matching what the player
 *   expects), we GROUP events that share the same (attacker, defender,
 *   fromTerritoryId, toTerritoryId) and whose `ts` values fall within
 *   BLITZ_GROUP_WINDOW_MS of the latest event in the batch. The group is
 *   rendered as one badge with summed losses, using the latest ts as the
 *   animation start so stale events don't fire a second animation.
 */

import React from 'react'
import { AppState } from 'react-native'
import type { GameEvent } from '@warcarp/api-types'
import { useDeferredAnimation } from './useDeferredAnimation'
import { attackResultColor } from '../lib/attackResultColor'

type AttackEvent = Extract<GameEvent, { kind: 'attack' }>

export interface AttackAnimationEntry {
  attackerTerritoryId: string
  defenderTerritoryId: string
  attackerLost: number
  defenderLost: number
  attackerColor: string
  defenderColor: string
  /** When true, the defender's territory flash is owned by ConquestFlashLayer. */
  conquered: boolean
  /** epoch-ms when the animation was queued — used to clean up stale entries */
  startedAt: number
  /** Unique within this hook instance; timestamps alone are not unique. */
  animationId?: string
}

/**
 * Pure diff function — exported for unit tests.
 *
 * Returns all `attack` events whose `ts` is strictly greater than `lastSeenTs`.
 */
export function detectNewAttacks(
  nextEvents: GameEvent[] | undefined,
  lastSeenTs: number,
): AttackEvent[] {
  if (!nextEvents || nextEvents.length === 0) return []
  return nextEvents.filter(
    // Fog of war: skip attacks with an end out of sight ('') — logged, not animated.
    (e): e is AttackEvent => e.kind === 'attack' && e.ts > lastSeenTs && e.fromTerritoryId !== '' && e.toTerritoryId !== '',
  )
}

/**
 * Seed an animation session from the current wire snapshot. Used on mount,
 * reconnect, and foreground return so already-buffered attacks are not
 * mistaken for newly-arrived attacks.
 */
export function animationBaseline(events: GameEvent[] | undefined): {
  lastSeenTs: number
  seenTs: Set<number>
} {
  const lastSeenTs = events && events.length > 0 ? Math.max(...events.map((e) => e.ts)) : 0
  const seenTs = new Set<number>()
  for (const event of events ?? []) {
    if (event.ts <= lastSeenTs) seenTs.add(event.ts)
  }
  return { lastSeenTs, seenTs }
}

/**
 * Time window (ms) within which attack events on the same territory pair are
 * treated as a single blitz request and their losses are summed. The engine
 * runs blitz rolls synchronously so events within the same HTTP request share
 * nearly identical ts values; 2000ms gives generous headroom while still
 * keeping distinct manual attacks (one per player action) separate.
 */
const BLITZ_GROUP_WINDOW_MS = 2000

/**
 * Group a flat list of fresh attack events into per-territory-pair groups.
 * Events on the same (attacker, defender, from, to) whose ts falls within
 * BLITZ_GROUP_WINDOW_MS of the group's latest event are merged.
 *
 * Returns one summed entry per group. Exported for unit tests.
 */
export interface AttackGroup {
  attacker: string
  defender: string
  fromTerritoryId: string
  toTerritoryId: string
  attackerLost: number
  defenderLost: number
  conquered: boolean
  /** ts of the latest event in the group — used as the animation start anchor. */
  latestTs: number
}

export function groupAttackEvents(events: AttackEvent[]): AttackGroup[] {
  const groups: AttackGroup[] = []

  for (const e of events) {
    const existing = groups.find(
      (g) =>
        g.attacker === e.attacker &&
        g.defender === e.defender &&
        g.fromTerritoryId === e.fromTerritoryId &&
        g.toTerritoryId === e.toTerritoryId &&
        Math.abs(e.ts - g.latestTs) <= BLITZ_GROUP_WINDOW_MS,
    )
    if (existing) {
      existing.attackerLost += e.attackerLost
      existing.defenderLost += e.defenderLost
      existing.conquered = existing.conquered || e.conquered
      existing.latestTs = Math.max(existing.latestTs, e.ts)
    } else {
      groups.push({
        attacker: e.attacker,
        defender: e.defender,
        fromTerritoryId: e.fromTerritoryId,
        toTerritoryId: e.toTerritoryId,
        attackerLost: e.attackerLost,
        defenderLost: e.defenderLost,
        conquered: e.conquered,
        latestTs: e.ts,
      })
    }
  }

  return groups
}

/** Duration the animation runs (ms). After this + buffer, the entry is removed.
 * Full-motion timeline: 150ms pop-in + 600ms hold + 400ms fade = 1150ms.
 * Reduced-motion: 100ms + 600ms + 100ms = 800ms. Use the longer value, plus the
 * 200ms buffer docs/spectator-animations.md specifies, so the sweep never cuts a
 * fade short — which it was doing while the burst lines held 1000ms. */
const ANIM_DURATION_MS = 1350

export function useAttackEvents(
  events: GameEvent[] | undefined,
  slotByPlayerId: Record<string, number>,
  playerColorHex: (slot: number) => string,
  /**
   * WS-session epoch. A changed value means the cache may contain events that
   * arrived while this device was disconnected; those events are historical
   * and must not replay as fresh animations.
   */
  viewerJoinedAt?: number,
): AttackAnimationEntry[] {
  const lastSeenTsRef = React.useRef<number | null>(null)
  const animatedTsSet = React.useRef<Set<number>>(new Set())
  const connectionEpochRef = React.useRef(viewerJoinedAt)
  const animationSequenceRef = React.useRef(0)
  const animationEpochRef = React.useRef(0)
  const appActiveRef = React.useRef(AppState.currentState === 'active')
  const baselineAfterBackgroundRef = React.useRef(false)
  const [appStateEpoch, setAppStateEpoch] = React.useState(0)

  const [entries, setEntries] = React.useState<AttackAnimationEntry[]>([])
  const defer = useDeferredAnimation()

  React.useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      const active = nextState === 'active'
      appActiveRef.current = active
      baselineAfterBackgroundRef.current = true
      animationEpochRef.current += 1
      if (!active) setEntries([])
      // Make the active transition run the baseline effect even when no new
      // game frame arrived at exactly the same time.
      setAppStateEpoch((epoch) => epoch + 1)
    })
    return () => subscription.remove()
  }, [])

  React.useEffect(() => {
    // Native may buffer several WS frames while the app is backgrounded. Do
    // not turn those historical frames into a catch-up animation flood.
    if (!appActiveRef.current) return

    // A reconnect can refill the cache with attacks that happened while this
    // device was away. They are historical for this animation session, not
    // fresh attacks to replay. Reset the baseline and clear queued badges when
    // the socket epoch changes; phase remounts keep the same epoch and still
    // use the max-ts baseline below.
    if (connectionEpochRef.current !== viewerJoinedAt) {
      connectionEpochRef.current = viewerJoinedAt
      animationEpochRef.current += 1
      const baseline = animationBaseline(events)
      lastSeenTsRef.current = baseline.lastSeenTs
      animatedTsSet.current = baseline.seenTs
      setEntries([])
      return
    }

    if (baselineAfterBackgroundRef.current) {
      baselineAfterBackgroundRef.current = false
      const baseline = animationBaseline(events)
      lastSeenTsRef.current = baseline.lastSeenTs
      animatedTsSet.current = baseline.seenTs
      setEntries([])
      return
    }

    if (lastSeenTsRef.current === null) {
      // Baseline = the highest ts already present in the snapshot at mount.
      // EVERY event present at mount is treated as already-seen; only events
      // that ARRIVE after this mount animate.
      //
      // Why not viewerJoinedAt? Each turn phase renders its own MapView
      // subtree (placement → attack remounts this hook with a fresh,
      // null lastSeenTsRef). The previous `viewerJoinedAt - 1` baseline made
      // every such remount treat the whole session's prior attacks as
      // "unseen" — they were skipped on mount but then flushed all at once
      // on the next events change (the player's FIRST attack of the turn),
      // so a turn that followed several bot turns showed a burst of unrelated
      // loss badges. Anchoring to maxTs (matching useConquestEvents) makes a
      // remount inert: bot attacks already animated live in the phase MapView
      // that was mounted while they happened, and won't replay. Genuinely new
      // events still arrive with a higher ts and animate normally.
      const baseline = animationBaseline(events)
      lastSeenTsRef.current = baseline.lastSeenTs
      // Pre-populate animatedTsSet with everything at/below the baseline (i.e.
      // every event present now) so the dedup guard doesn't fire if the same
      // ts arrives again via a WS push or cache replay.
      animatedTsSet.current = baseline.seenTs
      return
    }

    const newAttacks = detectNewAttacks(events, lastSeenTsRef.current)
    if (newAttacks.length === 0) return

    const maxNewTs = Math.max(...newAttacks.map((e) => e.ts))
    lastSeenTsRef.current = Math.max(lastSeenTsRef.current, maxNewTs)

    const fresh = newAttacks.filter((e) => !animatedTsSet.current.has(e.ts))
    if (fresh.length === 0) return

    for (const e of fresh) animatedTsSet.current.add(e.ts)

    // Group per-roll events from blitz attacks into one cumulative entry per
    // territory pair. A single manual attack produces a group of size 1, so
    // the display is identical to the previous behaviour in the common case.
    const groups = groupAttackEvents(fresh)

    const now = Date.now()
    const scheduledEpoch = animationEpochRef.current
    const newEntries: AttackAnimationEntry[] = groups.map((g) => {
      return {
        attackerTerritoryId: g.fromTerritoryId,
        defenderTerritoryId: g.toTerritoryId,
        attackerLost: g.attackerLost,
        defenderLost: g.defenderLost,
        attackerColor: attackResultColor(g.attacker, slotByPlayerId, playerColorHex),
        defenderColor: attackResultColor(g.defender, slotByPlayerId, playerColorHex),
        conquered: g.conquered,
        startedAt: now,
        animationId: `${now}-${animationSequenceRef.current++}`,
      }
    })

    // Deferred so the setState re-render does not compete with a tap gesture
    // still resolving on this frame — that is what gives the animation a clean
    // first frame on iOS. Scheduled through useDeferredAnimation, which cancels
    // only on UNMOUNT: cancelling in this effect's own cleanup is what silently
    // ate the attack badges, because the effect is keyed on `events` and the
    // callers pass `gameState.events` — a new array identity on every state
    // patch, so the next patch abandoned the dispatch before it could run.
    defer.afterInteractions(() => {
      if (animationEpochRef.current !== scheduledEpoch) return
      setEntries((prev) => [...prev, ...newEntries])
    })

    defer.later(() => {
      if (animationEpochRef.current !== scheduledEpoch) return
      const cutoff = now
      setEntries((prev) => prev.filter((e) => e.startedAt > cutoff))
    }, ANIM_DURATION_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, viewerJoinedAt, appStateEpoch])

  return entries
}
